import { Box } from '@mui/material';
import React, { useContext } from 'react';
import { Line } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend
} from 'chart.js';
import { formatChartDate, calculateYAxisConfig } from '../../helpers/chartHelpers';
import { ToolContext } from '../../../../../core/context/ToolContext';
import { SnakeLoader } from '../../../../../core/loader/SnakeLoader';

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend
);

export const LikesAndPostsChart = () => {
    const { social_media_insight_state } = useContext(ToolContext)
    const likesPostsData = social_media_insight_state.insightData.data;
    const likesAndPosts = likesPostsData?.likes_and_posts || [];
    const labels = likesAndPosts.map(item => formatChartDate(item.date, item.end_date));
    const likesData = likesAndPosts.map(item => item.likes);
    const postsData = likesAndPosts.map(item => item.posts);
    const maxValue = Math.max(...likesData, ...postsData);
    const yAxisConfig = calculateYAxisConfig([maxValue]);
    const maxLikes = Math.max(...likesData);
    const maxPosts = Math.max(...postsData);
    const yMax = Math.ceil(Math.max(maxLikes, maxPosts) * 1.1);
    const yTickCount = 7;
    let yStep = yTickCount > 1 ? (yMax - 0) / (yTickCount - 1) : 1;
    yStep = Math.ceil(yStep);
    const adjustedYMax = yStep * (yTickCount - 1);

    const data = {
        labels,
        datasets: [
            {
                label: 'Likes',
                data: likesData,
                borderColor: 'rgb(67, 124, 244)',
                backgroundColor: 'rgba(67, 124, 244, 0.1)',
                tension: 0.35,
                fill: false,
                borderWidth: 2,
                pointStyle: 'circle',
                pointRadius: 0,
                pointHoverRadius: 6,
                pointBackgroundColor: 'rgb(67, 124, 244)',
                pointHoverBackgroundColor: 'rgb(67, 124, 244)',
                pointBorderColor: '#fff',
                pointHoverBorderColor: '#fff',
                pointBorderWidth: 2,
                pointHoverBorderWidth: 2,
            },
            {
                label: 'Posts',
                data: postsData,
                borderColor: 'rgb(255, 99, 165)',
                backgroundColor: 'rgba(255, 99, 165, 0.1)',
                tension: 0.35,
                fill: false,
                borderWidth: 2,
                pointStyle: 'circle',
                pointRadius: 0,
                pointHoverRadius: 6,
                pointBackgroundColor: 'rgb(255, 99, 165)',
                pointHoverBackgroundColor: 'rgb(255, 99, 165)',
                pointBorderColor: '#fff',
                pointHoverBorderColor: '#fff',
                pointBorderWidth: 2,
                pointHoverBorderWidth: 2,
            }
        ]
    };

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: false,
            },
            tooltip: {
                backgroundColor: '#fff',
                padding: 12,
                cornerRadius: 18,
                displayColors: false,
                titleFont: {
                    size: 0
                },
                bodyFont: {
                    size: 14,
                    weight: '600',
                },
                borderColor: '#634ADD',
                borderWidth: 1,
                callbacks: {
                    title: () => '',
                    label: (context) => `${context.parsed.y} ${context.dataset.label}`,
                },
                bodyColor: '#000000',
                position: 'nearest',
                intersect: false,
                mode: 'index',
            }
        },
        scales: {
            x: {
                grid: {
                    display: true,
                    color: 'rgba(0, 0, 0, 0.05)',
                    drawBorder: false
                },
                ticks: {
                    color: '#666',
                    padding: 5
                }
            },
            y: {
                ...yAxisConfig,
                grid: {
                    color: 'rgba(0, 0, 0, 0.05)',
                    drawBorder: false
                },
                border: {
                    display: false
                },
                min: 0,
                max: adjustedYMax,
                ticks: {
                    color: '#666',
                    padding: 5,
                    stepSize: yStep,
                    callback: value => Number.isInteger(value) ? value : ''
                }
            }
        }
    };

    const legendItems = [
        {
            color: 'rgb(67, 124, 244)',
            label: 'Total Likes'
        },
        {
            color: 'rgb(255, 99, 165)',
            label: 'Total Posts'
        }
    ];

    return (
        <Box mb={3}>
            <Box sx={{
                display: 'flex',
                gap: '14px',
                alignItems: 'center',
                padding: '12px 30px 0 0 !important',
                justifyContent: 'end'
            }}>
                {legendItems.map((item, index) => (
                    <Box
                        key={index}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '5px',
                            fontSize: '12px',
                            color: '#666',
                        }}
                    >
                        <Box
                            sx={{
                                width: 10,
                                height: 10,
                                borderRadius: '50%',
                                backgroundColor: item.color
                            }}
                        />
                        {item.label}
                    </Box>
                ))}
            </Box>
            <Box sx={{ height: '370px', position: 'relative', backgroundColor: 'white', padding: '0 8px', borderRadius: '8px' }}>
                {(social_media_insight_state.insightLoading.data || social_media_insight_state.topContentsLoading.data) ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', width: '100%', fontSize: '14px', fontWeight: '600', color: '#666' }}>
                        <SnakeLoader size="1rem" bg="#11783C" distance="0.3rem" />
                    </ Box>
                ) :
                    (likesData.length > 0 || postsData.length > 0) ? (
                        <Line options={options} data={data} />
                    ) : (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', width: '100%', fontSize: '14px', fontWeight: '600', color: '#666' }}>
                            No data available
                        </Box>
                    )}
            </Box>
        </Box>
    );
};